import cx from 'classnames';
import { Location } from 'history';
import {
    GROUP_ASK_TO_JOIN,
    GROUP_ASKED_TO_JOIN,
    GROUP_YOU_ARE_BLOCKED,
    JOIN_GROUP,
    LEAVE_GROUP,
    num_POSTS_AND_ANSWERS_IN_LAST_WEEK,
} from 'mk/autogenerated/translations/GroupHeader.03c26c004c5503fb8f151213454c063d'
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import { BlogPostEntity, GroupEntity, GroupMemberEntity } from 'mk2/apps/blogs/schemas';
import { GroupCoverForm, GroupCoverFormData } from 'mk2/apps/groups/components/GroupCoverForm';
import { groupsGroupInfoUrl } from 'mk2/apps/groups/urls';
import { loginUrl } from 'mk2/apps/users/urls';
import Btn, { BtnCountType, BtnType } from 'mk2/components/Btn';
import Cover from 'mk2/components/Cover';
import PhotosFieldNew from 'mk2/components/forms/PhotosFieldNew';
import Interpolate from 'mk2/components/Interpolate';
import LoadingPlaceholder from 'mk2/components/LoadingPlaceholder';
import { FormPageType } from 'mk2/constants';
import { PhotosUploadConfig } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import React from 'react';
import styles from './GroupHeader.mscss';

export const GROUP_COVER_PHOTOS_CONFIG = {
    photoType: 'group_cover',
    multiple: false,
    showPhotoCode: false,
};

interface OwnProps {
    currentFormPage: FormPageType;
    group: GroupEntity;
    groupLoadingState: LoadingState;
    hasAskedForMembership: boolean;
    isBlocked: boolean;
    isJoining: boolean;
    isMember: boolean;
    isModerator: boolean;
    isSearchOpen: boolean;
    isSubscribed: boolean;
    isFavorite: boolean;
    isSubscribing: boolean;
    location: Location;
    moderators: GroupMemberEntity[];
    permissions: string[];
    posts: BlogPostEntity[];
    recentContributors: number;
    requestUser: UserEntity;

    onAddToFavorites(groupId: number, userId: number);
    onGroupCoverRemove(groupId: number);
    onGroupCoverSave(values: GroupCoverFormData, groupId: number);
    onGroupCoverToggleMenuMore();
    onInviteUser(groupId: number);
    onJoin(groupId: number, username: string);
    onLeave(groupId: number, username: string);
    onSubscribe(groupId: number, username: string);
    onRemoveFromFavorites(groupId: number, userId: number);
    onUnsubscribe(groupId: number, username: string);
    onUploadFiles(groupId: number, config: PhotosUploadConfig, files: File[]);
    onSearchClick();
}

interface State {
    showFullDescription: boolean;
}

export default class GroupHeader extends React.PureComponent<OwnProps, State> {

    private photosFieldRef: PhotosFieldNew;

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            showFullDescription: false,
        };
    }

    public renderPhotosField() {
        return (
            <PhotosFieldNew
                name={'photos'}
                config={GROUP_COVER_PHOTOS_CONFIG}
                ref={this.handlePhotosFileInputFieldRef}
                onUploadFiles={this.handleUploadFiles}
            />
        );
    }

    public renderGroupCover() {
        const { group, recentContributors } = this.props;

        return (
            <Cover
                icon={group.locked ? 'lock' : 'unlock-alt'}
                photo={group.coverPhoto}
                title={group.name}
                subtitle={(recentContributors > 0) && (
                    <Interpolate
                        i18nKey={num_POSTS_AND_ANSWERS_IN_LAST_WEEK}
                        num={<span className="number">{recentContributors}</span>}
                        count={recentContributors}
                    />
                )}
            />
        );
    }

    public render() {
        const {
            currentFormPage, group, groupLoadingState, hasAskedForMembership, isBlocked, isFavorite, isJoining,
            isMember, isModerator, isSearchOpen, isSubscribed, isSubscribing, location, onGroupCoverRemove,
            onGroupCoverSave, onGroupCoverToggleMenuMore, onSearchClick, permissions, requestUser,
        } = this.props;

        const isAdmin = permissions.indexOf('forum.can_manage_forum') >= 0;
        const showInviteBtn = !group.isDomainGroup && (!group.locked || (group.locked && isModerator));
        const groupLoaded = isSuccess(groupLoadingState);

        return (
            <div className={styles.GroupHeader}>
                <GroupCoverForm
                    group={group}
                    groupCover={this.renderGroupCover()}
                    photosField={this.renderPhotosField()}
                    groupLoadingState={groupLoadingState}
                    requestUser={requestUser}
                    permissions={permissions}
                    isModerator={isModerator}
                    onToggleMenuMore={onGroupCoverToggleMenuMore}
                    onRemove={onGroupCoverRemove}
                    onOpenFileExplorer={this.handleOpenFileExplorer}
                    onSave={onGroupCoverSave}
                />
                {currentFormPage !== FormPageType.ChangeGroupCover && (
                    <React.Fragment>
                        <div className={styles.GroupHeader__footer}>
                            {!group.deletedAt && isMember && (
                                <Btn
                                    key="subscribe"
                                    type={BtnType.LightGreyOutline}
                                    icon={isSubscribed ? 'fa-bell' : 'fa-bell-o'}
                                    onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                                    classNames={{label: styles.GroupHeader__bold}}
                                    className={cx(
                                        styles.GroupHeader__subscribe,
                                        {[styles['GroupHeader__subscribe--on']]: isSubscribed},
                                    )}
                                    disabled={isSubscribing}
                                />
                            )}
                            {!group.deletedAt && isBlocked && (
                                <div className={styles.GroupHeader__footer__blocked}>
                                    {GROUP_YOU_ARE_BLOCKED}
                                </div>
                            )}
                            {!group.deletedAt && !isBlocked && isMember && (
                                <Btn
                                    key="favorite"
                                    type={BtnType.LightGreyOutline}
                                    icon={isFavorite ? 'fa-star' : 'fa-star-o'}
                                    onClick={!isFavorite ? this.handleOnAddToFavorites : this.handleOnRemoveFromFavorites}
                                    classNames={{label: styles.GroupHeader__bold}}
                                    className={
                                        cx(styles.GroupHeader__favorite,
                                        {[styles['GroupHeader__favorite--on']]: isFavorite},
                                    )}
                                    disabled={!groupLoaded}
                                />

                            )}
                            {!group.deletedAt && !isBlocked && (
                                <Btn
                                    key="join"
                                    type={(isMember || hasAskedForMembership) ? BtnType.LightGreyOutline : BtnType.BlueOutline}
                                    label={
                                        isMember ? LEAVE_GROUP : (
                                            hasAskedForMembership ? GROUP_ASKED_TO_JOIN :
                                                (group.locked ? GROUP_ASK_TO_JOIN : JOIN_GROUP)
                                        )}
                                    count={isMember ? undefined : group.membersCount}
                                    countType={BtnCountType.Label}
                                    onClick={isAuthenticated(requestUser) ? (isMember ? this.handleOnLeave : this.handleOnJoin) : null}
                                    link={!isAuthenticated(requestUser) ? url(loginUrl, {},  {next: location.pathname}) : null}
                                    classNames={{label: styles.GroupHeader__bold}}
                                    className={isMember
                                        ? styles['GroupHeader__join--isMember']
                                        : styles['GroupHeader__join--isNotMember']}
                                    canBeVisuallyDisabled={!isModerator}
                                    hasSpinner={isJoining}
                                    disabled={isJoining || hasAskedForMembership}
                                />
                            )}
                            {(isMember || isAdmin) && (
                                <div key="rightButtons" className={styles.GroupHeader__rightButtons}>
                                    {isMember && !group.deletedAt && showInviteBtn && (
                                        <Btn
                                            key="add-member"
                                            type={BtnType.BlueTextLight}
                                            icon="fa-user-plus fa-lg"
                                            onClick={this.handleInviteUser}
                                        />
                                    )}
                                    <Btn
                                        key="about"
                                        className={!isMember && styles['GroupHeader__rightButtons__btn--admin']}
                                        type={BtnType.BlueTextLight}
                                        icon="fa-info-circle fa-lg"
                                        link={url(groupsGroupInfoUrl, { groupId: group.id })}
                                    />
                                    {!group.deletedAt && (
                                        <Btn
                                            key="search"
                                            className={cx(
                                                !isMember && styles['GroupHeader__rightButtons__btn--admin'],
                                                isSearchOpen && styles['GroupHeader__rightButtons__search--active'],
                                            )}
                                            type={BtnType.BlueTextLight}
                                            icon="fa-search fa-lg"
                                            onClick={onSearchClick}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </React.Fragment>
                )}
            </div>
        );
    }

    private handleInviteUser = () => {
        const { group, onInviteUser } = this.props;
        onInviteUser(group.id);
    };

    private handleOnJoin = () => {
        const { group, requestUser, onJoin } = this.props;
        onJoin(group.id, requestUser.username);
    };

    private handleOnLeave = () => {
        const { group, requestUser, onLeave } = this.props;
        onLeave(group.id, requestUser.username);
    };

    private handleOnSubscribe = () => {
        const { group, onSubscribe, requestUser } = this.props;
        onSubscribe(group.id, requestUser.username);
    };

    private handleOnUnsubscribe = () => {
        const { group, onUnsubscribe, requestUser } = this.props;
        onUnsubscribe(group.id, requestUser.username);
    };

    private handleOnAddToFavorites = () => {
        const { group, onAddToFavorites, requestUser } = this.props;
        onAddToFavorites(group.id, requestUser.id);
    };

    private handleOnRemoveFromFavorites = () => {
        const { group, onRemoveFromFavorites, requestUser } = this.props;
        onRemoveFromFavorites(group.id, requestUser.id);
    };

    private handleOpenFileExplorer = () => {
        // no photos yet, show file open dialog
        this.photosFieldRef.openFileExplorer();
    };

    private handlePhotosFileInputFieldRef = (ref: PhotosFieldNew) => {
        this.photosFieldRef = ref;
    };

    private handleUploadFiles = (config: PhotosUploadConfig, files: File[]) => {
        const { group, onUploadFiles } = this.props;
        onUploadFiles(group.id, config, files);
    };

    private handleOnToggleShowDetails = () => {
        this.setState((prevState) => ({ showFullDescription: !prevState.showFullDescription}));
    };
}

interface PlaceholderOwnProps {
    isLast?: boolean;
}

export const GroupHeaderPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({ isLast }) => (
    <div className={cx(styles.GroupHeader, styles.GroupHeaderPlaceholder)}>
        <div className={styles.GroupHeader__cover}>
            <div className={cx(styles.GroupHeader__cover_photo, styles.GroupHeaderPlaceholder__cover_photo)} />
            <div className={styles.GroupHeader__cover_text}>
                <LoadingPlaceholder className={styles.GroupHeader__cover_group_name} width={180} height={20} />
                <LoadingPlaceholder className={styles.GroupHeader__cover_posts} width={130} height={14} />
            </div>
        </div>
        <div className={styles.GroupHeader__footer}>
            <div className={cx(styles.GroupHeader__footer__subscribe, styles.GroupHeaderPlaceholder__subscribe)}>
                <LoadingPlaceholder width={120} height={25} />
            </div>
            <div className={styles.GroupHeaderPlaceholder__about}>
                <LoadingPlaceholder width={65} height={25} />
            </div>
        </div>
    </div>
);
GroupHeaderPlaceholder.displayName = 'GroupHeaderPlaceholder';
